import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { PiggyBank, Clock, AlertTriangle, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AccountantShareView — public, read-only, token-gated. No login.
 * Complete accountant pack: summary, categorisation, ledger, mileage and receipt evidence.
 */
export default function AccountantShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/freedom/tax/accountant-view/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Link not valid."));
  }, [token]);

  const pence = (p) => `£${((p || 0) / 100).toFixed(2)}`;

  if (err) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md rounded-2xl bg-white border border-red-200 p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto" />
          <p className="mt-3 text-lg font-extrabold text-slate-900">Link not valid</p>
          <p className="text-xs text-slate-500 mt-1">{err}</p>
          <p className="text-[11px] text-slate-400 mt-4">Please ask your instructor for a new link.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <p className="text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="accountant-share-view" className="min-h-dvh no-overflow-x bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-5 py-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">National Driving Guild · Accountant view</p>
          <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900">{data.instructor_name}</h1>
              <p className="text-sm text-slate-500">Tax year {data.tax_year} · {data.period.from} → {data.period.to}</p>
            </div>
            <a
              data-testid="download-accountant-pack-pdf"
              href={`${API}/api/freedom/tax/accountant-view/${token}/pack.pdf`}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-extrabold text-white shadow-sm hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Download PDF
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 py-6 space-y-4">
        {/* Headline set-aside */}
        <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <PiggyBank className="w-5 h-5 text-amber-600" />
            <p className="text-xs font-extrabold uppercase tracking-wider text-amber-800">Suggested set-aside · estimate only</p>
          </div>
          <p data-testid="share-set-aside" className="text-4xl font-extrabold text-amber-900">{pence(data.set_aside_pence)}</p>
          <p className="text-xs text-amber-700 mt-1">
            At {(data.rate_bps / 100).toFixed(2)}% of estimated profit {pence(data.net_before_tax_pence)}
          </p>
          <p className="text-[10px] text-amber-600 mt-2 leading-snug">
            Single-percentage estimate — does not calculate exact Income Tax + Class 2/4 NI.
          </p>
        </div>

        {/* Numbers */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <h2 className="text-sm font-extrabold text-slate-900">Summary</h2>
          <dl className="mt-3 divide-y divide-slate-100 text-sm">
            <Row label="Turnover (paid lessons)" value={pence(data.income_pence)} />
            <Row label="Expenses (excl. mileage)" value={pence(data.expenses_pence)} />
            <Row label={`Mileage (${data.total_miles} miles × 45p)`} value={pence(data.mileage_pence)} />
            <Row label="Estimated profit before tax" value={pence(data.net_before_tax_pence)} bold />
          </dl>
        </div>

        {/* HMRC-mapped breakdown */}
        {data.hmrc_breakdown.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <h2 className="text-sm font-extrabold text-slate-900 mb-3">Expenses mapped to HMRC Self-Assessment</h2>
            <table data-testid="share-hmrc-table" className="w-full text-xs">
              <thead>
                <tr className="text-slate-400 text-left">
                  <th className="pb-2 font-bold">HMRC category</th>
                  <th className="pb-2 font-bold">Platform</th>
                  <th className="pb-2 font-bold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.hmrc_breakdown.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-2 text-slate-700">{r.hmrc_category}</td>
                    <td className="py-2 text-slate-500 capitalize">{r.platform_category.replace("_", " ")}</td>
                    <td className="py-2 text-right font-bold text-slate-900">{pence(r.amount_pence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Full underlying records */}
        <div data-testid="share-expense-ledger" className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-extrabold text-slate-900">Expense ledger</h2>
              <p className="mt-1 text-xs text-slate-500">Every recorded expense, whether spoken, typed or photographed.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600">{data.expense_records?.length || 0}</span>
          </div>
          {(data.records_need_review || 0) > 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-800">
              {data.records_need_review} {data.records_need_review === 1 ? "entry needs" : "entries need"} checking before filing.
            </div>
          )}
          {!data.expense_records?.length ? (
            <p className="mt-4 text-xs text-slate-500">No expenses recorded for this tax year.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[560px] text-xs">
                <thead>
                  <tr className="text-left text-slate-400">
                    <th className="pb-2">Date</th>
                    <th className="pb-2">Category</th>
                    <th className="pb-2">Notes</th>
                    <th className="pb-2">Evidence</th>
                    <th className="pb-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.expense_records.map((item) => (
                    <tr key={item.id} className="border-t border-slate-100">
                      <td className="py-2.5 whitespace-nowrap text-slate-600">{item.date}</td>
                      <td className="py-2.5 capitalize text-slate-700">{(item.category || "other").replace("_", " ")}</td>
                      <td className="py-2.5 max-w-[180px] text-slate-500">{item.notes || "—"}{item.provisional ? " · Check" : ""}</td>
                      <td className="py-2.5">
                        {item.receipt_url ? (
                          <a href={`${API}${item.receipt_url}`} target="_blank" rel="noreferrer" className="font-bold text-blue-700 underline">View receipt</a>
                        ) : <span className="text-slate-400">Recorded only</span>}
                      </td>
                      <td className="py-2.5 text-right font-bold text-slate-900">{pence(item.amount_pence)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div data-testid="share-mileage-ledger" className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-extrabold text-slate-900">Mileage ledger</h2>
              <p className="mt-1 text-xs text-slate-500">Every business mileage entry behind the allowance total.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600">{data.mileage_records?.length || 0}</span>
          </div>
          {!data.mileage_records?.length ? (
            <p className="mt-4 text-xs text-slate-500">No mileage recorded for this tax year.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[460px] text-xs">
                <thead>
                  <tr className="text-left text-slate-400">
                    <th className="pb-2">Date</th>
                    <th className="pb-2">Purpose</th>
                    <th className="pb-2">Notes</th>
                    <th className="pb-2 text-right">Miles</th>
                    <th className="pb-2 text-right">Allowance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mileage_records.map((item) => (
                    <tr key={item.id} className="border-t border-slate-100">
                      <td className="py-2.5 whitespace-nowrap text-slate-600">{item.date}</td>
                      <td className="py-2.5 capitalize text-slate-700">{item.purpose || "other"}</td>
                      <td className="py-2.5 max-w-[180px] text-slate-500">{item.notes || "—"}</td>
                      <td className="py-2.5 text-right font-bold text-slate-900">{item.miles}</td>
                      <td className="py-2.5 text-right font-bold text-slate-900">{pence(item.value_pence)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-slate-100 border border-slate-200 p-4 text-[11px] text-slate-500 flex items-start gap-2">
          <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <p>
            This is an <strong>estimated</strong> live summary from the instructor's accounting tool. It is not tax or financial advice. HMRC Income Tax and Class 2/4 NI rates vary each year — please confirm the exact figures before filing. Link expires {new Date(data.expires_at).toLocaleDateString()}.
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between py-2">
      <dt className="text-slate-600">{label}</dt>
      <dd className={`text-slate-900 ${bold ? "font-extrabold" : "font-bold"}`}>{value}</dd>
    </div>
  );
}
